import { useFeatureGate } from '@/features/billing/gates';
import {
  getSubscriptionHealth,
  isSubscriptionInterrupted,
} from '@/features/billing/lib/subscriptionHealth';
import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import { type Organization, useOrganizations } from '@/hooks/useOrganizations';
import {
  ExperienceTypes,
  OrganizationType,
  UserTier,
} from '@/types/gql.generated';

/**
 * The FAMILY org that supplies this experience's text-message recipients, or
 * null if there isn't one. Child experiences fall back to the parent planner's
 * home org, matching how the server resolves recipients.
 *
 * Kept separate from the access check below because the recipient picker's
 * "manage members" shortcut needs the org itself — its id to deep-link settings
 * to, its name to label the button — not just whether messaging is available.
 */
export const useEventMessagesOrganization = (): Organization | null => {
  const { experience, parentExperience } = usePageExperienceContext();
  const { organizations } = useOrganizations();

  const organizationId =
    experience.organizationId ?? parentExperience?.organizationId;

  return (
    organizations.find(
      (organization) =>
        organization.id === organizationId &&
        organization.type === OrganizationType.Family
    ) ?? null
  );
};

/**
 * Every funded family workspace the signed-in user belongs to, whether or not
 * this experience is homed in one. Distinct from `useEventMessagesOrganization`,
 * which answers "which roster do this experience's reminders go to" — this one
 * answers "does this user have a family workspace at all", which is what
 * separates an upsell from a misplaced planner.
 *
 * Exported as the list rather than the first match because the misplaced copy
 * names one of these *and* the share modal offers them as move destinations:
 * a business workspace would take the planner without being able to send from
 * it, so it must not be among the choices.
 */
export const useUserFamilyWorkspaces = (): Organization[] => {
  const { sharedOrganizations } = useOrganizations();

  return sharedOrganizations.filter(
    (organization) =>
      organization.type === OrganizationType.Family &&
      organization.effectiveTier === UserTier.Pro
  );
};

type EventMessagesAccess = {
  /** Text reminders work here — render the real thing. */
  isEnabled: boolean;
  /** Show them locked, with an upgrade path, instead of hiding them. */
  isTeased: boolean;
  /**
   * Entitled, but not here: the user has a family workspace and this planner is
   * homed in their personal one. There is nothing to sell them — point them at
   * moving the planner instead.
   */
  isMisplaced: boolean;
  /**
   * This family workspace had text reminders and its plan has lapsed or stopped
   * paying. Show the surface read-only with a reactivation path rather than
   * hiding it: they have messages already scheduled here, and making the whole
   * feature vanish takes those out of view along with it.
   */
  isLapsed: boolean;
  /**
   * The profile query behind `isLapsed` hasn't resolved yet, so a `false` above
   * may still become true. Consumers that *remove* things when the feature is
   * unavailable — URL normalization especially — must wait for this.
   */
  isLoading: boolean;
};

/**
 * Whether an experience's events can carry text reminders, and if not, what to say
 * about it instead of showing nothing.
 *
 * Entitlement comes from the `textMessages` gate (a family-plan capability read off
 * the experience's home org). Everything else here is about *support*: the feature
 * only exists on planners, and not in a read-only page.
 *
 * The tease is deliberately limited to personal workspaces. Family orgs get the
 * working feature, business orgs will never get it at all (the gate reports them
 * `isHidden`), so a personal workspace is the only place an upsell makes sense —
 * and only for someone who has no family workspace yet. Someone who already has one
 * is `isMisplaced` instead: they bought this, their planner is just in the wrong
 * workspace. Child experiences fall back to the parent planner's home org.
 */
export const useEventMessagesEnabled = (): EventMessagesAccess => {
  const { experience, parentExperience, readOnly } = usePageExperienceContext();

  const effectiveExperience = parentExperience ?? experience;
  const { isGated, isLocked, isHidden } = useFeatureGate('textMessages', {
    experience: effectiveExperience,
  });
  // The experience's own `org` carries only tiers, not subscription state, so the
  // health read goes through the profile's copy of the same org — which means
  // `isLapsed` can't be trusted until that query has resolved.
  const messagesOrganization = useEventMessagesOrganization();
  const familyWorkspaces = useUserFamilyWorkspaces();
  const { isLoading } = useOrganizations();

  const isSupported =
    !readOnly && experience.type === ExperienceTypes.Planner && !isHidden;

  const isPersonalOrg =
    effectiveExperience.org?.type === OrganizationType.Personal;

  // Supported here, but the personal workspace it's homed in can't have it. Which
  // of the two things to say about that depends on whether they have a family
  // workspace to be pointed at.
  const isPersonalGap = isSupported && isGated && isPersonalOrg;

  return {
    // `isLocked`, not `!isGated`: entitlement is still unknown while the profile
    // loads, and opening the real panel then would show messages to someone who may
    // not be entitled to them.
    isEnabled: isSupported && !isLocked,
    // The upsell waits for the profile: the workspace list reads as empty until
    // it lands, so selling on it early would flash "upgrade to Family" at someone
    // who already owns one.
    isTeased: isPersonalGap && !isLoading && familyWorkspaces.length === 0,
    isMisplaced: isPersonalGap && familyWorkspaces.length > 0,
    // A family workspace that lost entitlement isn't an upsell target the way a
    // personal one is — they bought this once. Distinguished from `isTeased` so
    // the copy can say "reactivate" rather than "upgrade", and so the panel can
    // keep showing the messages already scheduled here.
    isLapsed:
      isSupported &&
      isGated &&
      !isPersonalOrg &&
      isSubscriptionInterrupted(getSubscriptionHealth(messagesOrganization)),
    isLoading,
  };
};
